import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { PagedServerTable, TableRequest } from '@components/Table';
import { ROUTES } from '@shared/constants';
import { ROLES } from '@containers/Admin/shared/roles';
import { USER_PAGE_TYPES } from '../UserPage/consts';
import { getColumns } from './config';
import { getUsersTable, getUsersTableOptions } from './service';
import { UsersTableOptions, UsersTableRow } from './types';
import useVideoChatV2 from './useVideoChatV2';
import { Page, TableFrame, Title } from './styles';

const EMPTY_OPTIONS: UsersTableOptions = {
  businessUnits: [],
  mentors: [],
};

const UsersTable = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { roles } = useSelector(
    (state: RootStateOrAny) => state.getIn(['app', 'authentication', 'user']).toJS(),
  );
  const [options, setOptions] = useState<UsersTableOptions>(EMPTY_OPTIONS);
  const { confirmVideoChat, pendingUserId, isStarting } = useVideoChatV2();
  const optionsRequestRef = useRef<Promise<void> | null>(null);
  const optionsControllerRef = useRef<AbortController | null>(null);
  const showBusinessUnits = roles.includes(ROLES.TECH_ADMIN);
  const isTherapist = [
    ROLES.CLINICAL_MENTOR_HEB,
    ROLES.EMOTIONAL_MENTOR_HEB,
    ROLES.CLINICAL_MENTOR_EN,
    ROLES.EMOTIONAL_MENTOR_EN,
    ROLES.DEFAULT_MENTOR,
  ].some((role) => roles.includes(role));
  const loadOptions = useCallback(() => {
    if (!optionsRequestRef.current) {
      const controller = new AbortController();
      optionsControllerRef.current = controller;
      optionsRequestRef.current = getUsersTableOptions(controller.signal)
        .then(setOptions)
        .catch(() => {
          if (!controller.signal.aborted) optionsRequestRef.current = null;
        });
    }
  }, []);
  const fetchData = useCallback(
    (request: TableRequest) => getUsersTable(request),
    [],
  );
  const openUser = useCallback((user: UsersTableRow) => {
    history.push(generatePath(ROUTES.USERS.PAGE, {
      id: user.id,
      pageType: USER_PAGE_TYPES.INFORMATION,
    }));
  }, [history]);
  const columns = useMemo(
    () => getColumns({
      filterOptions: options,
      showBusinessUnits,
      loadOptions,
      isTherapist,
      pendingVideoUserId: pendingUserId,
      isStartingVideo: isStarting,
      onStartVideoChat: confirmVideoChat,
      onOpenRecords: openUser,
      t,
    }),
    [confirmVideoChat, isStarting, isTherapist, loadOptions, openUser, options, pendingUserId, showBusinessUnits, t],
  );

  useEffect(() => () => optionsControllerRef.current?.abort(), []);

  return (
    <Page>
      <Title>Users</Title>
      <TableFrame>
        <PagedServerTable
          columns={columns}
          fetchData={fetchData}
          getRowKey={(row) => row.id}
          pageSize={10}
          initialSort={{ column: 'NAME', direction: 'ASC' }}
          showFilterSummary
          itemLabel="users"
          emptyMessage="No users found."
          filteredEmptyMessage="No users match these filters."
          loadingMessage="Loading users..."
          errorMessage="Could not load users."
          onRowClick={openUser}
        />
      </TableFrame>
    </Page>
  );
};

export default UsersTable;